import { agentforgeApiQuery } from '../index';
import type {
  MCDashboard,
  MCAgent,
  AgentActivity,
  DelegationRecord,
  ActivityFilters,
  DelegationFilters,
} from '@/types/mission-control';

interface PaginatedResponse<T> {
  data: T[];
  total: number;
}

export const missionControlApi = agentforgeApiQuery.injectEndpoints({
  endpoints: (builder) => ({
    getDashboard: builder.query<MCDashboard, void>({
      query: () => ({ url: '/mission-control/dashboard' }),
      providesTags: ['MCDashboard'],
    }),

    getAgents: builder.query<MCAgent[], void>({
      query: () => ({ url: '/mission-control/agents' }),
      transformResponse: (response: { agents: MCAgent[] }) =>
        response.agents || [],
      providesTags: ['MCAgents'],
    }),

    getActivities: builder.query<
      PaginatedResponse<AgentActivity>,
      ActivityFilters
    >({
      query: ({
        agentId,
        kulaJobId,
        type,
        since,
        limit = 50,
        offset = 0,
      } = {}) => ({
        url: '/mission-control/activities',
        params: {
          agent_id: agentId,
          kula_job_id: kulaJobId,
          type,
          since,
          limit,
          offset,
        },
      }),
      transformResponse: (response: {
        activities: AgentActivity[];
        total: number;
      }) => ({
        data: response.activities || [],
        total: response.total || 0,
      }),
      providesTags: ['MCActivities'],
    }),

    getDelegations: builder.query<
      PaginatedResponse<DelegationRecord>,
      DelegationFilters
    >({
      query: ({
        status,
        fromAgentId,
        toAgentId,
        limit = 50,
        offset = 0,
      } = {}) => ({
        url: '/mission-control/delegations',
        params: {
          status,
          from_agent_id: fromAgentId,
          to_agent_id: toAgentId,
          limit,
          offset,
        },
      }),
      transformResponse: (response: {
        delegations: DelegationRecord[];
        total: number;
      }) => ({
        data: response.delegations || [],
        total: response.total || 0,
      }),
      providesTags: ['MCDelegations'],
    }),

    getDelegation: builder.query<DelegationRecord, string>({
      query: (delegationId) => ({
        url: `/mission-control/delegations/${delegationId}`,
      }),
      transformResponse: (response: { delegation: DelegationRecord }) =>
        response.delegation,
    }),

    getDelegationTree: builder.query<DelegationRecord[], string>({
      query: (sessionId) => ({
        url: `/mission-control/delegation-tree/${sessionId}`,
      }),
      transformResponse: (response: { delegations: DelegationRecord[] }) =>
        response.delegations || [],
    }),
  }),
});

export const {
  useGetDashboardQuery,
  useGetAgentsQuery,
  useGetActivitiesQuery,
  useGetDelegationsQuery,
  useGetDelegationQuery,
  useGetDelegationTreeQuery,
} = missionControlApi;
